import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type JSX,
  type ReactNode,
} from "react";

import { HtmlContent } from "@vega-core/components/HtmlContent/HtmlContent";
import { TooltipInfoIcon } from "@vega-core/components/Tooltip/TooltipInfoIcon";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { htmlToPlainText } from "@vega-core/utils/richText";

type Props = {
  content?: ReactNode;
  checked: boolean;
  description?: JSX.Element | string;
  disabled?: boolean;
  inputStyle?: string;
  isInlineList?: boolean;
  variant?: "default" | "outlined";
  labelVariant?: "compact" | "default" | "transparent";
  name: string;
  onBlur?: FocusEventHandler;
  onChange?: ChangeEventHandler;
  setChange?: (field: string, value: boolean) => void;
  title: JSX.Element | string;
  tooltip?: {
    title?: string;
    content?: string;
  };
  tooltipContentClassname?: string;
  value: string;
  visibleDescription?: boolean;
  fullWidth?: boolean;
};

export function RadioField({
  content,
  isInlineList,
  checked,
  description,
  disabled = false,
  inputStyle,
  name,
  onBlur,
  setChange,
  title,
  tooltip,
  tooltipContentClassname,
  value,
  visibleDescription = false,
  labelVariant = "default",
  variant = "default",
  fullWidth = false,
}: Props) {
  const inputId = `${name}${value}`;

  return (
    <div
      className={cnTwMerge(
        "flex flex-col items-center transition justify-start",
        {
          "bg-dark-gray p-4":
            labelVariant !== "compact" && variant !== "outlined",
          "pr-0 pl-3 py-4 text-justify": fullWidth,
          "h-full": variant === "outlined",
        },
      )}
      key={value}
    >
      <div
        className={cnTwMerge("flex flex-col items-start gap-3 w-full", {
          "h-full": variant === "outlined",
        })}
      >
        <label
          className={cnTwMerge("flex", {
            "cursor-pointer": !disabled,
            "cursor-not-allowed opacity-50": disabled,
            "border border-border/primary rounded-lg p-4 w-full h-full min-h-28 items-start":
              variant === "outlined",
          })}
          htmlFor={inputId}
          onClick={() => {
            if (!disabled) {
              setChange && setChange(value, checked);
            }
          }}
        >
          <input
            aria-label={`Radio ${name} ${typeof title === "string" ? htmlToPlainText(title) : title}`}
            checked={checked}
            className={cnTwMerge(
              "mr-3 h-[18px] w-[18px] shrink-0 border-[1.5px] border-solid border-neutral-700 bg-transparent checked:bg-brand-default focus:ring-transparent focus:ring-offset-0",
              inputStyle,
              {
                "cursor-pointer": !disabled,
                "cursor-not-allowed": disabled,
                "hover:!bg-brand-default focus:!bg-brand-default":
                  checked && !disabled,
                "mt-0.5": variant === "outlined",
              },
            )}
            data-testid={`${name}-radio-${value}`}
            disabled={disabled}
            id={inputId}
            name={name}
            onBlur={onBlur}
            onChange={() => {
              setChange && setChange(value, checked);
            }}
            type="radio"
            value={value}
          />
          <div
            className="flex w-full items-center justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="flex gap-2">
                <div
                  className={cnTwMerge("body-base-regular block", {
                    "cursor-pointer": !disabled,
                    "cursor-not-allowed": disabled,
                    "body-base-medium": variant === "outlined",
                    "pr-8": fullWidth,
                  })}
                >
                  <HtmlContent content={title} />
                </div>
                {isTruthy(tooltip) && (
                  <TooltipInfoIcon
                    content={tooltip.content}
                    title={tooltip.title}
                    tooltipContentClassname={tooltipContentClassname}
                  />
                )}
              </div>
              {isTruthy(description) && (visibleDescription || checked) ? (
                <div
                  className={cnTwMerge(
                    "mt-2 body-base-regular text-text/disabled",
                    { "text-text/secondary": variant === "outlined" },
                  )}
                >
                  {description}
                </div>
              ) : null}
            </div>
          </div>
        </label>
        {isTruthy(content) && checked && !isInlineList ? (
          <div className="pl-0 sm:pl-8 w-full">{content}</div>
        ) : null}
      </div>
    </div>
  );
}
